import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Home,
  BookOpen,
  Video,
  Play,
  BarChart3,
  Users,
  Settings,
  Shield,
  ChevronDown,
  ChevronRight,
  User,
  FileText,
  Calendar,
  TrendingUp,
  Gift,
  Menu,
  X
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../utils/cn';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import { ADMIN_MENU_ITEMS } from '../../constants/adminModules';

const Sidebar = () => {
  const [expandedMenus, setExpandedMenus] = useState({});
  const [isCollapsed, setIsCollapsed] = useState(false);
  const { user } = useAuth();
  const location = useLocation();
  
  const toggleMenu = (menuKey) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuKey]: !prev[menuKey]
    }));
  };
  
  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };
  
  const isMenuActive = (paths) => {
    return paths.some(path => isActive(path));
  };
  
  // Navigation items based on user role
  const getNavigationItems = () => {
    const baseItems = [
      {
        key: 'dashboard',
        label: 'Dashboard',
        icon: Home,
        path: '/dashboard'
      }
    ];
    
    if (user?.role === 'student') {
      return [
        ...baseItems,
        {
          key: 'tests',
          label: 'Tests',
          icon: BookOpen,
          children: [
            { label: 'Results', path: '/test-results' },
            { label: 'Practice', path: '/practice-tests' }
          ]
        },
        { key: 'meetings', label: 'Meetings', icon: Calendar, path: '/meetings' },
        { key: 'games', label: 'Games', icon: Play, path: '/games' },
        { key: 'progress', label: 'Progress', icon: TrendingUp, path: '/analytics/progress' },
        {
          key: 'profile',
          label: 'Profile',
          icon: User,
          path: '/profile'
        }
      ];
    }
    
    if (user?.role === 'teacher') {
      return [
        ...baseItems,
        {
          key: 'communication',
          label: 'Communication',
          icon: Calendar,
          children: [
            { label: 'Messages', path: '/messages' },
            { label: 'Meetings', path: '/meetings' },
            { label: 'Notifications', path: '/notifications' }
          ]
        },
        {
          key: 'students',
          label: 'Students',
          icon: Users,
          children: [
            { label: 'List', path: '/students' },
            { label: 'Progress', path: '/students/progress' }
          ]
        },
        {
          key: 'analytics',
          label: 'Analytics',
          icon: BarChart3,
          children: [
            { label: 'Performance', path: '/analytics/performance' },
            { label: 'Progress', path: '/analytics/progress' }
          ]
        },
        {
          key: 'profile',
          label: 'Profile',
          icon: User,
          path: '/profile'
        }
      ];
    }
    
    if (user?.role === 'parent') {
      return [
        ...baseItems,
        {
          key: 'children',
          label: 'My Children',
          icon: Users,
          children: [
            { label: 'Progress', path: '/children/progress' },
            { label: 'Results', path: '/children/results' },
            { label: 'Meetings', path: '/meetings' }
          ]
        },
        {
          key: 'analytics',
          label: 'Analytics',
          icon: BarChart3,
          children: [
            { label: 'Performance', path: '/analytics/performance' },
            { label: 'Progress', path: '/analytics/progress' },
            { label: 'Insights', path: '/analytics/insights' }
          ]
        },
        {
          key: 'communication',
          label: 'Communication',
          icon: Calendar,
          children: [
            { label: 'Messages', path: '/messages' },
            { label: 'Meetings', path: '/meetings' },
            { label: 'Notifications', path: '/notifications' }
          ]
        },
        {
          key: 'profile',
          label: 'Profile',
          icon: User,
          path: '/profile'
        }
      ];
    }
    
    if (user?.role === 'referral') {
      return [
        ...baseItems,
        {
          key: 'referrals',
          label: 'Referral Dashboard',
          icon: Gift,
          path: '/referrals'
        },
        {
          key: 'profile',
          label: 'Profile',
          icon: User,
          path: '/profile'
        }
      ];
    }

    const iconMap = { users: Users, communication: Calendar, content: FileText, analytics: BarChart3, referrals: Users, rewards: Gift, system: Settings };
    if (user?.role === 'admin' || user?.role === 'staff') {
      const allowedPaths = user?.role === 'staff' && Array.isArray(user?.allowedModules) ? user.allowedModules : null;
      const items = ADMIN_MENU_ITEMS.map((section) => {
        let children = section.children || [];
        if (user?.role === 'admin') {
          children = children.filter((c) => !c.staffOnly);
        } else if (user?.role === 'staff') {
          children = allowedPaths
            ? children.filter((c) => allowedPaths.includes(c.path) || c.staffOnly)
            : children.filter((c) => c.staffOnly);
        }
        if (children.length === 0) return null;
        return {
          key: section.key,
          label: section.label,
          icon: iconMap[section.key] || FileText,
          children
        };
      }).filter(Boolean);
      return [...baseItems, ...items];
    }
    
    return baseItems;
  };
  
  const navigationItems = getNavigationItems();
  
  const renderMenuItem = (item) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedMenus[item.key];
    const isItemActive = hasChildren 
      ? isMenuActive(item.children.map(child => child.path))
      : isActive(item.path);
    
    if (hasChildren) {
      return (
        <div key={item.key}>
          <button
            className={cn(
              "w-full flex items-center justify-between p-3 rounded-lg text-left transition-colors",
              "hover:bg-slate-100",
              isItemActive ? "bg-slate-100 text-exam font-medium" : "text-slate-600"
            )}
            onClick={() => toggleMenu(item.key)}
          >
            <div className="flex items-center space-x-3">
              <item.icon className="w-5 h-5 shrink-0 text-slate-500" />
              {!isCollapsed && (
                <span>{item.label}</span>
              )}
            </div>
            {!isCollapsed && (
              isExpanded ? 
                <ChevronDown className="w-4 h-4 text-slate-400" /> : 
                <ChevronRight className="w-4 h-4 text-slate-400" />
            )}
          </button>
          
          {isExpanded && !isCollapsed && (
            <div className="ml-4 mt-1 pl-4 border-l border-slate-200 space-y-0.5">
              {item.children.map((child, index) => (
                <Link
                  key={index}
                  to={child.path}
                  className={cn(
                    "flex items-center justify-between py-2 px-2 rounded-md text-sm transition-colors",
                    isActive(child.path)
                      ? "bg-slate-100 text-exam font-medium"
                      : "text-slate-600 hover:bg-slate-50"
                  )}
                >
                  <span>{child.label}</span>
                  {child.badge && (
                    <Badge variant={child.badge.variant} size="small">
                      {child.badge.text}
                    </Badge>
                  )}
                </Link>
              ))}
            </div>
          )}
        </div>
      );
    }
    
    return (
      <Link
        key={item.key}
        to={item.path}
        className={cn(
          "flex items-center justify-between p-3 rounded-lg transition-colors",
          isItemActive
            ? "bg-slate-100 text-exam font-medium"
            : "text-slate-600 hover:bg-slate-100"
        )}
      >
        <div className="flex items-center space-x-3">
          <item.icon className="w-5 h-5 shrink-0 text-slate-500" />
          {!isCollapsed && (
            <span>{item.label}</span>
          )}
        </div>
        {item.badge && !isCollapsed && (
          <Badge variant={item.badge.variant} size="small">
            {item.badge.text}
          </Badge>
        )}
      </Link>
    );
  };
  
  return (
    <div className={cn(
      "bg-white border-r border-slate-200 transition-all duration-200 flex flex-col shrink-0",
      isCollapsed ? "w-[4.25rem]" : "w-60"
    )}>
      <div className="flex items-center justify-end p-3 border-b border-slate-200">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="p-2"
        >
          {isCollapsed ? <Menu className="w-4 h-4" /> : <X className="w-4 h-4" />}
        </Button>
      </div>

      <nav className="flex-1 overflow-y-auto p-3 space-y-0.5">
        {navigationItems.map(renderMenuItem)}
      </nav>

      {user && (
        <div className="p-3 border-t border-slate-200">
          {!isCollapsed ? (
            <div className="bg-slate-50 rounded-lg p-3">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-exam rounded-full flex items-center justify-center shrink-0">
                  <User className="w-4 h-4 text-white" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-slate-900 truncate">
                    {user.firstName} {user.lastName}
                  </p>
                  <p className="text-xs text-slate-500 capitalize">
                    {user.role}
                  </p>
                </div>
              </div>
              <div className="flex gap-1 mt-3 pt-3 border-t border-slate-200">
                <Link 
                  to="/profile" 
                  className="p-2 text-slate-500 hover:text-slate-700 rounded-lg hover:bg-slate-200/50 transition-colors"
                  title="Profile"
                >
                  <Settings className="w-4 h-4" />
                </Link>
                <Link 
                  to="/help" 
                  className="p-2 text-slate-500 hover:text-slate-700 rounded-lg hover:bg-slate-200/50 transition-colors"
                  title="Help"
                >
                  <Shield className="w-4 h-4" />
                </Link>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <div className="w-9 h-9 bg-exam rounded-full flex items-center justify-center">
                <User className="w-4 h-4 text-white" />
              </div>
              <Link to="/profile" className="p-2 text-slate-500 hover:bg-slate-100 rounded-lg" title="Profile">
                <Settings className="w-4 h-4" />
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Sidebar;